import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Container,
  Divider,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  LinearProgress,
  CircularProgress,
  Fade,
  Collapse,
  Alert,
} from '@mui/material';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { extractErrorMessage } from '../utils/errorHandler';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CodeIcon from '@mui/icons-material/Code';

const Login: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [passwordStrength, setPasswordStrength] = useState<number>(0);
  const [usernameError, setUsernameError] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { signIn, signInGuest, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const usernameInputRef = useRef<HTMLInputElement>(null);
  
  // Auto-focus username field on mount
  useEffect(() => {
    usernameInputRef.current?.focus();
  }, []);
  useEffect(() => {
    if (!password) {
      setPasswordStrength(0);
      return;
    }
    
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 25;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 15;
    if (/[^a-zA-Z0-9]/.test(password)) strength += 10;
    
    setPasswordStrength(Math.min(strength, 100));
  }, [password]);
  
  const validateUsername = (value: string): string => {
    if (!value) return 'Username is required';
    if (value.length < 3) return 'Username must be at least 3 characters';
    return '';
  };
  
  const validatePassword = (value: string): string => {
    if (!value) return 'Password is required';
    if (value.length < 5) return 'Password must be at least 5 characters';
    return '';
  };
  
  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUsername(value);
    setUsernameError('');
    setError(null);
  };
  
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    setPasswordError('');
    setError(null);
  };
  
  const getPasswordStrengthColor = () => {
    if (passwordStrength < 40) return '#dc3545';
    if (passwordStrength < 70) return '#ffc107';
    return '#28a745';
  };
  
  const getPasswordStrengthLabel = () => {
    if (passwordStrength < 40) return 'Weak';
    if (passwordStrength < 70) return 'Medium';
    return 'Strong';
  };
  
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  
  // If already authenticated, redirect to dashboard
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  const handleSignIn = async () => {
    setError(null);
    setIsMicrosoftLoading(true);
    try {
      await signIn();
      setSuccessMessage('Successfully signed in! Redirecting...');
      setTimeout(() => navigate('/'), 1000);
    } catch (error: any) {
      // Use backend error message directly
      const errorMessage = extractErrorMessage(error);
      setError(errorMessage);
      setIsMicrosoftLoading(false);
    }
  };

  const handleGuestLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate fields
    const userError = validateUsername(username);
    const passError = validatePassword(password);
    
    if (userError) {
      setUsernameError(userError);
      return;
    }
    
    if (passError) {
      setPasswordError(passError);
      return;
    }
    
    setError(null);
    setIsSubmitting(true);
    
    try {
      await signInGuest(username, password);
      setSuccessMessage('Login successful! Redirecting to dashboard...');
      setTimeout(() => navigate('/'), 1000);
    } catch (error: any) {
      // Use backend error message directly (no frontend message creation)
      const errorMessage = extractErrorMessage(error);
      setError(errorMessage);
      setIsSubmitting(false);
    }
  };
  
  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          width: '100%',
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '32px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          transition: 'box-shadow 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0 6px 30px rgba(0, 0, 0, 0.12)',
          }
        }}
      >
        <Typography 
          component="h1" 
          variant="h4" 
          sx={{ 
            fontWeight: 400, 
            mb: 4,
            fontSize: '1.75rem',
            color: '#000',
            textAlign: 'center'
          }}
        >
          Welcome to R2C Workbench
        </Typography>
        
        <Box sx={{ width: '100%' }}>
          {/* Success Message */}
          <Collapse in={!!successMessage}>
            <Alert 
              icon={<CheckCircleOutlineIcon />}
              severity="success" 
              sx={{ mb: 3 }}
            >
              {successMessage}
            </Alert>
          </Collapse>
          
          {/* Error Message */}
          <Collapse in={!!error}>
            <Box 
              sx={{ 
                border: '1px solid #dc3545',
                borderRadius: '4px',
                backgroundColor: '#f8d7da',
                color: '#721c24',
                p: 2,
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
              role="alert"
              aria-live="polite"
            >
              <ErrorOutlineIcon sx={{ color: '#dc3545' }} />
              <Typography variant="body2">{error}</Typography>
            </Box>
          </Collapse>
          
          <UnauthenticatedTemplate>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleSignIn}
              disabled={isLoading || isMicrosoftLoading}
              aria-label="Sign in with Microsoft account"
              sx={{ 
                py: 1.8,
                mb: 3,
                backgroundColor: 'rgba(14, 19, 25, 0.8)',
                color: '#fff',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                borderRadius: '4px',
                transition: 'all 0.3s ease',
                transform: 'translateY(0)',
                '&:hover': {
                  backgroundColor: 'rgba(14, 19, 25, 0.95)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(14, 19, 25, 0.3)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
                '&:disabled': {
                  backgroundColor: 'rgba(14, 19, 25, 0.4)',
                  color: '#ccc'
                },
                '&:focus-visible': {
                  outline: '3px solid rgba(14, 19, 25, 0.5)',
                  outlineOffset: '2px',
                }
              }}
            >
              {isLoading || isMicrosoftLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} sx={{ color: '#fff' }} />
                  <span>Signing in...</span>
                </Box>
              ) : (
                'Sign in with Microsoft'
              )}
            </Button>

            <Box sx={{ position: 'relative', mb: 3 }}>
              <Divider aria-hidden="true" />
              <Typography 
                variant="body2" 
                sx={{ 
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: '#fff',
                  px: 2,
                  color: '#999'
                }}
              >
                or
              </Typography>
            </Box>

            <Box 
              component="form" 
              onSubmit={handleGuestLogin} 
              sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
              noValidate
            >
              <Box>
                <Typography 
                  variant="body2" 
                  component="label" 
                  htmlFor="username-input"
                  sx={{ 
                    fontWeight: 600,
                    mb: 1,
                    display: 'block',
                    color: '#000'
                  }}
                >
                  Username
                </Typography>
                <TextField
                  id="username-input"
                  placeholder="Username"
                  variant="outlined"
                  fullWidth
                  value={username}
                  onChange={handleUsernameChange}
                  required
                  inputRef={usernameInputRef}
                  error={!!usernameError}
                  helperText={usernameError}
                  inputProps={{
                    'aria-label': 'Username',
                    'aria-required': 'true',
                    'aria-invalid': !!usernameError,
                    'aria-describedby': usernameError ? 'username-error' : undefined,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                      backgroundColor: '#fff',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#f8f9fa',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#fff',
                      },
                      '&.Mui-focusVisible': {
                        outline: '3px solid rgba(14, 19, 25, 0.2)',
                        outlineOffset: '2px',
                      }
                    }
                  }}
                />
              </Box>

              <Box>
                <Typography 
                  variant="body2" 
                  component="label" 
                  htmlFor="password-input"
                  sx={{ 
                    fontWeight: 600,
                    mb: 1,
                    display: 'block',
                    color: '#000'
                  }}
                >
                  Password
                </Typography>
                <TextField
                  id="password-input"
                  placeholder="Password"
                  type={showPassword ? 'text' : 'password'}
                  variant="outlined"
                  fullWidth
                  value={password}
                  onChange={handlePasswordChange}
                  required
                  error={!!passwordError}
                  helperText={passwordError}
                  inputProps={{
                    'aria-label': 'Password',
                    'aria-required': 'true',
                    'aria-invalid': !!passwordError,
                    'aria-describedby': passwordError ? 'password-error' : undefined,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                          tabIndex={0}
                          sx={{
                            '&:focus-visible': {
                              outline: '2px solid rgba(14, 19, 25, 0.5)',
                              outlineOffset: '2px',
                            }
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                      backgroundColor: '#fff',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#f8f9fa',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#fff',
                      },
                      '&.Mui-focusVisible': {
                        outline: '3px solid rgba(14, 19, 25, 0.2)',
                        outlineOffset: '2px',
                      }
                    }
                  }}
                />
                
                {password && (
                  <Fade in={!!password}>
                    <Box sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          Password strength:
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: getPasswordStrengthColor(),
                            fontWeight: 600
                          }}
                        >
                          {getPasswordStrengthLabel()}
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={passwordStrength} 
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: getPasswordStrengthColor(),
                            transition: 'all 0.3s ease',
                          }
                        }}
                        aria-label={`Password strength: ${getPasswordStrengthLabel()}`}
                      />
                    </Box>
                  </Fade>
                )}
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={!username || !password || isSubmitting}
                aria-label="Continue with guest login"
                sx={{ 
                  py: 1.8,
                  mt: 1,
                  backgroundColor: username && password ? 'rgba(14, 19, 25, 0.8)' : '#9ca3af',
                  color: '#fff',
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                  borderRadius: '4px',
                  transition: 'all 0.3s ease',
                  transform: 'translateY(0)',
                  '&:hover': {
                    backgroundColor: username && password ? 'rgba(14, 19, 25, 0.95)' : '#6b7280',
                    transform: username && password ? 'translateY(-2px)' : 'none',
                    boxShadow: username && password ? '0 4px 12px rgba(14, 19, 25, 0.3)' : 'none',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  '&:disabled': {
                    backgroundColor: '#9ca3af',
                    color: '#fff'
                  },
                  '&:focus-visible': {
                    outline: '3px solid rgba(14, 19, 25, 0.5)',
                    outlineOffset: '2px',
                  }
                }}
              >
                {isSubmitting ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} sx={{ color: '#fff' }} />
                    <span>Logging in...</span>
                  </Box>
                ) : (
                  'Continue'
                )}
              </Button>
            </Box>

          </UnauthenticatedTemplate>
          
          <AuthenticatedTemplate>
            <Typography variant="body1" color="text.primary" sx={{ textAlign: 'center' }}>
              You are already signed in. Redirecting...
            </Typography>
          </AuthenticatedTemplate>
        </Box>
      </Box>
    </Container>
  );
};

export default Login;